import type { StockCount, StockCountStatus, StockItemCategory } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ClipboardCheck, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { ReasonDialog } from '@/features/pos/ReasonDialog';
import { quantityLabel, signedQuantityLabel } from '@/features/stock/labels';
import {
  cancelStockCount,
  createStockCount,
  stockCountQueryOptions,
  stockCountsQueryOptions,
  stockKeys,
  stockLocationsQueryOptions,
  updateStockCountLines,
  validateStockCount,
} from '@/features/stock/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

interface CountsSearch {
  count?: string;
}

export const Route = createFileRoute('/_app/stock/counts')({
  validateSearch: (search: Record<string, unknown>): CountsSearch =>
    typeof search['count'] === 'string' && search['count'] ? { count: search['count'] } : {},
  component: StockCountsPage,
});

const CATEGORIES: StockItemCategory[] = ['BEVERAGE', 'FOOD', 'SUPPLY', 'OTHER'];
const STATUS_TONE: Record<StockCountStatus, 'amber' | 'green' | 'neutral'> = {
  OPEN: 'amber',
  VALIDATED: 'green',
  CANCELLED: 'neutral',
};

/** Inventaires physiques (ADR-020, D-H33, R-INV-06) : liste, ouverture, saisie et validation. */
function StockCountsPage() {
  const { t, i18n } = useTranslation(['stock', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { propertyId } = useCurrentProperty();
  const canRead = session.can('stock.read');
  const canCount = session.can('stock.count');
  const [openDialog, setOpenDialog] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [category, setCategory] = useState<StockItemCategory | ''>('');
  const [notes, setNotes] = useState('');
  const [validating, setValidating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [counted, setCounted] = useState<Record<string, string>>({});

  const list = useInfiniteQuery(stockCountsQueryOptions({ propertyId }, 25, canRead));
  const rows = useMemo(() => list.data?.pages.flatMap((page) => page.data) ?? [], [list.data]);
  const locations = useQuery(stockLocationsQueryOptions(propertyId, canRead));
  const detail = useQuery(
    stockCountQueryOptions(search.count ?? '', canRead && Boolean(search.count)),
  );
  const count = detail.data;

  useEffect(() => {
    if (!count) return;
    setCounted(
      Object.fromEntries(
        count.lines.map((line) => [
          line.itemId,
          line.countedQuantity === null ? '' : String(line.countedQuantity),
        ]),
      ),
    );
  }, [count]);

  useEffect(() => {
    if (openDialog) {
      setLocationId(locations.data?.[0]?.id ?? '');
      setCategory('');
      setNotes('');
    }
  }, [openDialog, locations.data]);

  const select = (id: string | undefined) =>
    void navigate({ search: id ? { count: id } : {}, replace: true });

  const create = useMutation({
    mutationFn: () =>
      createStockCount({
        locationId,
        ...(category ? { category } : {}),
        notes: notes.trim() || null,
      }),
    onSuccess: async (created) => {
      toast.success(t('stock:counts.toasts.opened'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setOpenDialog(false);
      select(created.id);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const save = useMutation({
    mutationFn: (current: StockCount) =>
      updateStockCountLines(current.id, {
        version: current.version,
        lines: current.lines.map((line) => {
          const raw = (counted[line.itemId] ?? '').trim();
          const value = raw ? Number(raw.replace(',', '.')) : null;
          return {
            itemId: line.itemId,
            countedQuantity: value !== null && Number.isFinite(value) ? value : null,
          };
        }),
      }),
    onSuccess: async () => {
      toast.success(t('stock:counts.toasts.saved'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const validate = useMutation({
    mutationFn: (current: StockCount) =>
      validateStockCount(current.id, { version: current.version }),
    onSuccess: async () => {
      toast.success(t('stock:counts.toasts.validated'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setValidating(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const cancel = useMutation({
    mutationFn: ({ current, reason }: { current: StockCount; reason: string }) =>
      cancelStockCount(current.id, { version: current.version, reason }),
    onSuccess: async () => {
      toast.success(t('stock:counts.toasts.cancelled'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setCancelling(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const dirty =
    count?.status === 'OPEN' &&
    count.lines.some(
      (line) =>
        (counted[line.itemId] ?? '') !==
        (line.countedQuantity === null ? '' : String(line.countedQuantity)),
    );

  return (
    <Container>
      <PageHeader
        title={t('stock:counts.title')}
        subtitle={t('stock:counts.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('stock:title'), link: <Link to="/stock">{t('stock:title')}</Link> },
          { label: t('stock:counts.title') },
        ]}
        actions={
          canCount && propertyId ? (
            <Button variant="primary" onClick={() => setOpenDialog(true)}>
              <Plus className="ico" aria-hidden="true" />
              {t('stock:counts.new')}
            </Button>
          ) : null
        }
      />

      {count ? (
        <Card>
          <CardHeader
            title={`${t('stock:counts.detail')} — ${count.reference}`}
            actions={
              <div className="row row--wrap" style={{ gap: 8 }}>
                <Badge tone={STATUS_TONE[count.status]}>
                  {t(`stock:counts.status.${count.status}`)}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => select(undefined)}>
                  {t('common:actions.close')}
                </Button>
              </div>
            }
          />
          <CardBody density="flush">
            <div className="row row--wrap muted t-13" style={{ gap: 12, padding: '8px 16px' }}>
              <span>{count.locationName}</span>
              <span>{formatDateTime(count.startedAt, i18n.language)}</span>
              <span>
                {t('stock:counts.progress', {
                  counted: count.countedLines,
                  total: count.lines.length,
                })}
              </span>
              {count.notes ? <span>{count.notes}</span> : null}
            </div>
            <TableWrap>
              <Table compact>
                <thead>
                  <tr>
                    <Th>{t('stock:levels.columns.item')}</Th>
                    <Th align="right">{t('stock:counts.expected')}</Th>
                    <Th align="right">{t('stock:counts.counted')}</Th>
                    <Th align="right">{t('stock:counts.variance')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {count.lines.map((line) => {
                    const raw = (counted[line.itemId] ?? '').trim();
                    const value = raw ? Number(raw.replace(',', '.')) : null;
                    const variance =
                      count.status === 'OPEN'
                        ? value !== null && Number.isFinite(value)
                          ? Math.round((value - line.expectedQuantity) * 1000) / 1000
                          : null
                        : line.variance;
                    return (
                      <Tr key={line.id}>
                        <Td>
                          <strong>{line.itemName}</strong>{' '}
                          <span className="t-12 muted">
                            {line.itemCode} · {t(`stock:unit.${line.unit}`)}
                          </span>
                        </Td>
                        <Td numeric align="right">
                          {quantityLabel(line.expectedQuantity, i18n.language)}
                        </Td>
                        <Td numeric align="right">
                          {count.status === 'OPEN' && canCount ? (
                            <Input
                              aria-label={`${t('stock:counts.counted')} — ${line.itemName}`}
                              inputMode="decimal"
                              value={counted[line.itemId] ?? ''}
                              style={{ width: 110, marginLeft: 'auto' }}
                              onChange={(event) =>
                                setCounted((current) => ({
                                  ...current,
                                  [line.itemId]: event.target.value,
                                }))
                              }
                            />
                          ) : line.countedQuantity === null ? (
                            '—'
                          ) : (
                            quantityLabel(line.countedQuantity, i18n.language)
                          )}
                        </Td>
                        <Td numeric align="right">
                          {variance === null ? (
                            '—'
                          ) : (
                            <Badge
                              tone={variance === 0 ? 'neutral' : variance < 0 ? 'red' : 'green'}
                            >
                              {signedQuantityLabel(variance, i18n.language)}
                            </Badge>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
            {count.status === 'OPEN' && canCount ? (
              <div
                className="row row--wrap"
                style={{ gap: 8, justifyContent: 'flex-end', padding: 12 }}
              >
                <Button variant="ghost" onClick={() => setCancelling(true)}>
                  {t('stock:counts.cancel')}
                </Button>
                <Button
                  variant="secondary"
                  loading={save.isPending}
                  disabled={!dirty}
                  onClick={() => save.mutate(count)}
                >
                  {t('stock:counts.save')}
                </Button>
                <Button
                  variant="primary"
                  disabled={dirty || count.countedLines === 0}
                  onClick={() => setValidating(true)}
                >
                  {t('stock:counts.validate')}
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody density="flush">
          {list.isPending ? (
            <TableSkeleton rows={4} columns={5} label={t('common:states.loading')} />
          ) : list.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(list.error, t)}
              traceId={errorTraceId(list.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void list.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ClipboardCheck className="ico" aria-hidden="true" />}
              title={t('stock:counts.empty')}
              description={t('stock:counts.emptyHint')}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('stock:counts.columns.reference')}</Th>
                    <Th>{t('stock:counts.columns.location')}</Th>
                    <Th>{t('stock:counts.columns.started')}</Th>
                    <Th>{t('stock:counts.columns.progress')}</Th>
                    <Th>{t('stock:counts.columns.status')}</Th>
                    <Th align="right">{t('common:actions.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Tr key={row.id}>
                      <Td className="mono">{row.reference}</Td>
                      <Td>{row.locationName}</Td>
                      <Td>{formatDateTime(row.startedAt, i18n.language)}</Td>
                      <Td>
                        {t('stock:counts.progress', {
                          counted: row.countedLines,
                          total: row.lines.length,
                        })}
                      </Td>
                      <Td>
                        <Badge tone={STATUS_TONE[row.status]}>
                          {t(`stock:counts.status.${row.status}`)}
                        </Badge>
                      </Td>
                      <Td align="right">
                        <Button size="sm" variant="ghost" onClick={() => select(row.id)}>
                          {t('stock:counts.open')}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent
          title={t('stock:counts.new')}
          icon={<ClipboardCheck className="ico" aria-hidden="true" />}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="secondary" type="button" onClick={() => setOpenDialog(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={!locationId || create.isPending}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                {t('stock:counts.new')}
              </Button>
            </>
          }
        >
          <div className="stack">
            <FormField label={t('stock:counts.location')} required>
              {(field) => (
                <NativeSelect
                  id={field.id}
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  {(locations.data ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>
            <FormField label={t('stock:counts.category')}>
              {(field) => (
                <NativeSelect
                  id={field.id}
                  value={category}
                  onChange={(event) => setCategory(event.target.value as StockItemCategory | '')}
                >
                  <option value="">{t('stock:counts.allCategories')}</option>
                  {CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`stock:category.${value}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>
            <FormField label={t('stock:counts.notes')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={notes}
                  maxLength={500}
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </FormField>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={validating} onOpenChange={setValidating}>
        <DialogContent
          title={t('stock:counts.validateTitle')}
          description={t('stock:counts.validateDescription')}
          icon={<ClipboardCheck className="ico" aria-hidden="true" />}
          iconTone="green"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="secondary" type="button" onClick={() => setValidating(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                type="button"
                loading={validate.isPending}
                onClick={() => count && validate.mutate(count)}
              >
                {t('common:actions.confirm')}
              </Button>
            </>
          }
        />
      </Dialog>

      <ReasonDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t('stock:counts.cancelTitle')}
        description={t('stock:counts.cancelDescription')}
        confirmLabel={t('stock:counts.cancel')}
        pending={cancel.isPending}
        onConfirm={(reason) => count && cancel.mutate({ current: count, reason })}
      />
    </Container>
  );
}
